// /inventory/stats — per-product breakdown across a date window.
// Polished to match the new institutional design (uses inv-* CSS classes).
//
// Columns: 产品 · 品类 · 口味 · 规格 · 班次 · 生产 · 售出 · 报废 · 报废率 · 售罄率
// + (admin only) 操作 column with a 重置数据 trash icon → wipes all events for that SKU.
//
// Features:
//   - Search box (filter by name / variant / category)
//   - Sortable column headers (click to toggle asc / desc)
//   - Shift toggle (全部 / 白班 / 晚班)
//   - Date range presets + custom range
//   - .xlsx pivot export matching the printed Excel template
//   - Admin-only delete: confirms via dialog, then wipes the SKU's events
//     (use case: clean up testing data before going live)

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api.ts';
import { displayName } from '../lib/products.ts';
import {
  torontoToday, torontoNDaysAgo,
  torontoMidnightUtcIso, torontoEndExclusiveUtcIso
} from '../lib/torontoTime.js';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { Toast } from '../components/atoms.jsx';
import { isElevated } from '../lib/auth.ts';
import { REPORT_LOCATIONS } from '../lib/locations.ts';
import { PrepAnalysis } from '../components/PrepAnalysis.jsx';
// exceljs is ~900 KB. Dynamic-import inside doExport so users who never click
// 「导出 Excel」 don't pay for it on initial load.

// Date helpers — operate in Toronto time (America/Toronto) since VAVA's 3
// stores + central kitchen are all in Toronto. When Regina opens the stats
// page at 9pm Toronto, "今天" means the Toronto calendar day. We send the
// API the UTC instant bounds of the Toronto day (via ?from_utc / ?to_utc)
// so the SQL window captures all events that happened during Toronto's
// calendar day, regardless of UTC offset (handles EDT/EST automatically).
function todayYMD()      { return torontoToday(); }
function nDaysAgoYMD(n)  { return torontoNDaysAgo(n); }

const PRESETS = [
  { key: '1d',   label: '今日',       from: todayYMD,              to: todayYMD },
  { key: '7d',   label: '最近 7 天',  from: () => nDaysAgoYMD(6),   to: todayYMD },
  { key: '30d',  label: '最近 30 天', from: () => nDaysAgoYMD(29),  to: todayYMD },
  { key: '90d',  label: '最近 90 天', from: () => nDaysAgoYMD(89),  to: todayYMD },
  { key: '180d', label: '最近半年',   from: () => nDaysAgoYMD(179), to: todayYMD }
];

const SHIFT_OPTIONS = [
  { key: 'all',   label: '全部',   exportSuffix: '全部' },
  { key: 'day',   label: '☀ 白班', exportSuffix: '白班' },
  { key: 'night', label: '🌙 晚班', exportSuffix: '晚班' }
];

// Per-store filter — added after Regina asked "统计要分门店". 'all' = aggregate
// across all locations (default). RH/DT/NY/CK = narrow to that one location.
// Note: 生产 only happens at CK; filtering to a storefront yields produced=0
// for every SKU (which is correct and informative — it shows what each
// storefront DID, not what arrived there).
const LOCATION_OPTIONS = [
  { key: 'all', label: '全部门店', exportSuffix: '全部' },
  { key: 'RH',  label: 'RH',     exportSuffix: 'RH' },
  { key: 'DT',  label: 'DT',     exportSuffix: 'DT' },
  { key: 'NY',  label: 'NY',     exportSuffix: 'NY' },
  { key: 'CK',  label: 'CK 中央厨房', exportSuffix: 'CK' }
];

// ---------- formatting -----------------------------------------------------
function formatRate(r) {
  if (r === null || r === undefined) return '—';
  if (r === 0) return '0%';
  const pct = r * 100;
  if (pct < 0.1) return '<0.1%';
  return pct.toFixed(1) + '%';
}
function wasteRateColor(r) {
  if (r === null || r === undefined || r === 0) return 'var(--vava-text-subtle)';
  if (r >= 0.10) return 'var(--vava-red)';
  if (r >= 0.05) return 'var(--vava-amber)';
  return 'var(--vava-green)';
}
function sellThroughColor(r) {
  if (r === null || r === undefined) return 'var(--vava-text-subtle)';
  if (r >= 0.90) return 'var(--vava-green)';
  if (r >= 0.70) return 'var(--vava-tan-hover)';
  return 'var(--vava-amber)';
}
// 可售 = 过去库存 + 当日生产 + 净调拨. 剩余 = 可售 − 售出 − 报废 (= on-hand at
// window end). The 调拨 term is what makes a single-store view correct: stock
// reaches a storefront via 调拨 from the kitchen, so without it RH's 剩余 ignored
// its incoming stock and went negative (Regina 2026-06-08). Globally 调拨 nets to
// 0, so the 全部门店 numbers are unchanged.
const availableOf = (t) => (t.opening || 0) + (t.produced || 0) + (t.transferNet || 0);
const remainingOf = (t) => availableOf(t) - (t.sold || 0) - (t.wasted || 0);
function remainingColor(n) {
  if (n === 0) return 'var(--vava-text-subtle)';
  return n < 0 ? 'var(--vava-red)' : 'var(--vava-text)';
}
// 调拨 (net transfer at this scope): +N moved in, −N moved out, 0 → '·'. Globally
// nets to 0; only meaningful when narrowed to a single store.
function fmtTransfer(n) {
  const v = n || 0;
  if (v === 0) return '·';
  return v > 0 ? `+${v}` : `−${Math.abs(v)}`;
}
function transferColor(n) {
  const v = n || 0;
  if (v === 0) return 'var(--vava-text-subtle)';
  return v > 0 ? 'var(--vava-green)' : 'var(--vava-tan-hover)';
}

// ---------- .xlsx export (pivot — rows × dates, cols × SKU hierarchy) -----
async function downloadXlsx({ pivot, fileLabel }) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'VAVA Production ERP';
  wb.created = new Date();

  const products = pivot.products;
  const dates = pivot.dates;

  function buildSheet(name, valueKey, summaryFn) {
    const ws = wb.addWorksheet(name, {
      views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }]
    });

    const headerRows = [
      ['日期', ...products.map(p => p.category || '—')],
      ['',     ...products.map(p => p.variant  || '—')],
      ['',     ...products.map(p => p.size || (p.variant ? '' : p.name))]
    ];
    headerRows.forEach((row, i) => {
      const r = ws.addRow(row);
      r.font = { bold: true };
      r.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      if (i === 0) r.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAEAEA' } };
    });
    ws.getCell('A1').value = '日期 / 品类 / 负责人';
    ws.mergeCells('A1:A3');
    ws.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

    function mergeRun(rowIdx, values) {
      let start = 2;
      for (let c = 3; c <= values.length + 1; c++) {
        if (values[c - 2] !== values[start - 2]) {
          if (c - 1 > start) ws.mergeCells(rowIdx, start, rowIdx, c - 1);
          start = c;
        }
      }
      if (values.length + 1 > start) {
        ws.mergeCells(rowIdx, start, rowIdx, values.length + 1);
      }
    }
    mergeRun(1, products.map(p => p.category || '—'));
    mergeRun(2, products.map(p => p.variant  || '—'));

    for (const date of dates) {
      const targetText = pivot.targets[date]?.targetText
        ?? (pivot.targets[date]?.targetUnits != null
              ? `目标 ${pivot.targets[date].targetUnits}` : '');
      const rowVals = [
        targetText ? `${date}\n${targetText}` : date,
        ...products.map(p => p.perDay[date]?.[valueKey] ?? 0)
      ];
      const r = ws.addRow(rowVals);
      r.alignment = { wrapText: true, vertical: 'top' };
    }

    const totals = ['合计', ...products.map(p => summaryFn(p))];
    const tot = ws.addRow(totals);
    tot.font = { bold: true };
    tot.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F7F7' } };

    ws.getColumn(1).width = 18;
    for (let i = 2; i <= products.length + 1; i++) ws.getColumn(i).width = 10;
  }

  buildSheet('生产', 'produced', p => p.totals.produced);
  buildSheet('售出', 'sold',     p => p.totals.sold);
  buildSheet('报废', 'wasted',   p => p.totals.wasted);

  const summary = wb.addWorksheet('汇总', {
    views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
  });
  summary.columns = [
    { header: '产品',   key: 'name',         width: 28 },
    { header: '品类',   key: 'category',     width: 14 },
    { header: '口味',   key: 'variant',      width: 12 },
    { header: '规格',   key: 'size',         width: 8 },
    { header: '班次',   key: 'shift',        width: 8 },
    { header: '生产',   key: 'produced',     width: 8 },
    { header: '售出',   key: 'sold',         width: 8 },
    { header: '报废',   key: 'wasted',       width: 8 },
    { header: '报废率', key: 'wasteRate',    width: 10 },
    { header: '售罄率', key: 'sellThrough',  width: 10 }
  ];
  summary.getRow(1).font = { bold: true };
  summary.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAEAEA' } };
  for (const p of products) {
    summary.addRow({
      name: p.name,
      category: p.category || '—',
      variant: p.variant || '—',
      size: p.size || '—',
      shift: p.shift === 'day' ? '白班' : p.shift === 'night' ? '晚班' : '通用',
      produced: p.totals.produced,
      sold: p.totals.sold,
      wasted: p.totals.wasted,
      wasteRate:   formatRate(p.wasteRate),
      sellThrough: formatRate(p.sellThrough)
    });
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vava-${fileLabel}-${pivot.from}_to_${pivot.to}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------- 前台当值汇报 .xlsx (one sheet per store; rows × date) -----------
// Counts only — the 收入 columns are present but empty until product prices
// land (header marks them 待价格). 前台人员 is best-effort: whoever logged the
// day's sales (attribution is mixed). 6寸 flavors = SKUs with size 6寸;
// cupcakes = SKUs whose category/name reads cup/cks.
async function downloadFrontdeskXlsx(report) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'VAVA';
  wb.created = new Date();

  // 6寸 sellable flavors are categorised 经典6寸 / Seasonal+2 6寸 (NOT 胚, the cake
  // bases, which are also 6" but never sold to customers). Cupcakes = category cks.
  const flavorProducts = report.products.filter(p => (p.category || '').includes('6寸'));
  const cupcakeProducts = report.products.filter(p => {
    const cat = (p.category || '').toLowerCase();
    return cat === 'cks' || /cupcake/i.test(`${p.category || ''} ${p.name || ''}`);
  });
  const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAEAEA' } };

  const stores = report.stores.length ? report.stores : ['—'];
  for (const store of stores) {
    const ws = wb.addWorksheet(`${store}前台`, { views: [{ state: 'frozen', xSplit: 2, ySplit: 1 }] });
    ws.columns = [
      { header: '日期', key: 'date', width: 12 },
      { header: '前台人员', key: 'staff', width: 14 },
      ...flavorProducts.map(p => ({ header: `6寸${p.variant || p.name}`, key: `f_${p.id}`, width: 12 })),
      { header: '6寸总销售量', key: 'total6', width: 12 },
      { header: 'Cupcakes', key: 'cupcakes', width: 10 },
      { header: '6寸总收入（待价格）', key: 'rev6', width: 16 },
      { header: 'Cupcake总收入（待价格）', key: 'revcc', width: 18 }
    ];
    const hr = ws.getRow(1);
    hr.font = { bold: true };
    hr.fill = HEADER_FILL;
    hr.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

    const soldStore = report.sold[store] || {};
    const actorsStore = report.actors[store] || {};
    for (const date of report.dates) {
      const day = soldStore[date];
      const total6 = flavorProducts.reduce((s, p) => s + (day?.[p.id] || 0), 0);
      const cupcakes = cupcakeProducts.reduce((s, p) => s + (day?.[p.id] || 0), 0);
      if (total6 === 0 && cupcakes === 0) continue;   // keep the sheet to active days
      const rowObj = {
        date,
        staff: (actorsStore[date] || []).join(' / ') || '—',
        total6, cupcakes, rev6: '', revcc: ''
      };
      for (const p of flavorProducts) rowObj[`f_${p.id}`] = day?.[p.id] || 0;
      ws.addRow(rowObj);
    }
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vava-前台汇报-${report.from}_to_${report.to}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ============================================================================
// InventoryStats
// ============================================================================

export const InventoryStats = ({ user }) => {
  // Default: 今日 (single-day report). Regina opens this page each day to
  // see what shipped/sold today; multi-day windows are available via preset
  // chips or the date pickers.
  const [mode, setMode] = useState('stock');   // 'stock' = 生产/售出/报废 · 'prep' = 备货分析
  const [preset, setPreset] = useState('1d');
  const [from, setFrom] = useState(() => todayYMD());
  const [to, setTo] = useState(() => todayYMD());
  const [shift, setShift] = useState('all');
  const [location, setLocation] = useState('all');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  // Default sort: 品类 ascending so same-category rows cluster together
  // (Regina's preferred view — easier to compare within a category).
  const [sort, setSort] = useState({ key: 'category', dir: 'asc' });
  const [report, setReport] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingFd, setExportingFd] = useState(false);   // 前台汇报 export
  const [wipeTarget, setWipeTarget] = useState(null);   // {id, name} for the row to wipe
  const [toast, setToast] = useState(null);
  // Tree expansion state for the category → SKU → location drill.
  // Keys: "cat:{categoryName}" and "sku:{productId}". Empty = all collapsed
  // (the default — show 11 category aggregate rows, drill on click).
  const [expanded, setExpanded] = useState(() => new Set());

  // "Elevated" = admin + 店长; both can wipe a SKU's history.
  const isAdmin = isElevated(user);

  function toggleExpand(key) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }
  function expandAll() {
    if (!report) return;
    const next = new Set();
    const catNames = new Set();
    for (const r of report.rows) {
      const cat = r.category || '—';
      catNames.add(cat);
      next.add(`cat:${cat}`);
      next.add(`sku:${r.id}`);
    }
    setExpanded(next);
  }
  function collapseAll() { setExpanded(new Set()); }

  async function load(
    fromArg = from, toArg = to, shiftArg = shift,
    locationArg = location, inactiveArg = includeInactive
  ) {
    setLoading(true); setErr('');
    try {
      const opts = { includeInactive: inactiveArg, breakdown: 'location', utc: true };
      if (shiftArg === 'day' || shiftArg === 'night') opts.shift = shiftArg;
      if (locationArg !== 'all') opts.location = locationArg;
      // Convert Toronto-local YMDs to UTC instant bounds. fromArg/toArg are
      // YYYY-MM-DD as picked in the date inputs (Toronto calendar dates).
      const fromUtc = torontoMidnightUtcIso(fromArg);
      const toUtc   = torontoEndExclusiveUtcIso(toArg);
      const r = await api.loadInventoryStatsByProduct(fromUtc, toUtc, opts);
      setReport(r);
    } catch (e) {
      setErr(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  function applyPreset(key) {
    const p = PRESETS.find(x => x.key === key);
    if (!p) return;
    const nf = p.from(); const nt = p.to();
    setPreset(key); setFrom(nf); setTo(nt);
    load(nf, nt, shift, location, includeInactive);
  }
  function applyCustom() {
    setPreset('custom');
    load(from, to, shift, location, includeInactive);
  }
  function changeShift(s) {
    setShift(s);
    load(from, to, s, location, includeInactive);
  }
  function changeLocation(l) {
    setLocation(l);
    load(from, to, shift, l, includeInactive);
  }
  function changeSort(key) {
    setSort(s => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));
  }

  // ===== Search filter, applied at the SKU level =====
  const filteredSkus = useMemo(() => {
    if (!report) return [];
    if (!search) return report.rows;
    const q = search.toLowerCase();
    return report.rows.filter(r =>
      r.name.toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q) ||
      (r.variant || '').toLowerCase().includes(q) ||
      (r.size || '').toLowerCase().includes(q)
    );
  }, [report, search]);

  // ===== Build the category → SKU tree from filtered SKUs =====
  // Each category aggregates its SKUs' produced/sold/wasted. SKUs within a
  // category sort by name (stable, scannable). Categories sort by the active
  // sort key (品类 / 生产 / 报废 / etc) so "biggest producer category first"
  // works if she clicks 生产.
  const tree = useMemo(() => {
    const byCat = new Map();
    for (const r of filteredSkus) {
      const cat = r.category || '—';
      let entry = byCat.get(cat);
      if (!entry) {
        entry = { name: cat, skus: [], totals: { produced: 0, sold: 0, wasted: 0, opening: 0, transferNet: 0 } };
        byCat.set(cat, entry);
      }
      entry.skus.push(r);
      entry.totals.produced    += r.produced;
      entry.totals.sold        += r.sold;
      entry.totals.wasted      += r.wasted;
      entry.totals.opening     += (r.opening || 0);
      entry.totals.transferNet += (r.transferNet || 0);
    }
    for (const c of byCat.values()) {
      const avail = availableOf(c.totals);
      c.totals.wasteRate   = c.totals.produced > 0 ? c.totals.wasted / c.totals.produced : null;
      c.totals.sellThrough = avail > 0 ? c.totals.sold / avail : null;
      c.skus.sort((a, b) => a.name.localeCompare(b.name, 'zh'));
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    const valOfCat = (c) => {
      switch (sort.key) {
        case 'category':    return c.name;
        case 'opening':     return c.totals.opening || 0;
        case 'transferNet': return c.totals.transferNet || 0;
        case 'produced':    return c.totals.produced;
        case 'sold':        return c.totals.sold;
        case 'wasted':      return c.totals.wasted;
        case 'remaining':   return remainingOf(c.totals);
        case 'wasteRate':   return c.totals.wasteRate   ?? -1;
        case 'sellThrough': return c.totals.sellThrough ?? -1;
        default:            return c.name;       // SKU-only keys: fall back to category
      }
    };
    return [...byCat.values()].sort((a, b) => {
      const av = valOfCat(a), bv = valOfCat(b);
      if (typeof av === 'string' && typeof bv === 'string')
        return av.localeCompare(bv, 'zh') * dir;
      return (av - bv) * dir;
    });
  }, [filteredSkus, sort]);

  // When searching, every category with at least one match auto-expands so
  // results are immediately visible. The user's manual collapses survive a
  // cleared search (we layer over the base `expanded` state, not replace it).
  const effectiveExpanded = useMemo(() => {
    if (!search) return expanded;
    const next = new Set(expanded);
    for (const cat of tree) next.add(`cat:${cat.name}`);
    return next;
  }, [expanded, search, tree]);

  // Build the flat list of rows to render. Each row is tagged with its kind
  // so the renderer can pick the right markup + indent + chevron.
  const flatRows = useMemo(() => {
    const out = [];
    const LOCS = REPORT_LOCATIONS;   // 统计 sums the two kitchens into one CK
    for (const cat of tree) {
      out.push({ kind: 'cat', cat });
      if (!effectiveExpanded.has(`cat:${cat.name}`)) continue;
      for (const sku of cat.skus) {
        out.push({ kind: 'sku', sku });
        if (!effectiveExpanded.has(`sku:${sku.id}`)) continue;
        for (const loc of LOCS) {
          const bucket = sku.byLocation?.[loc];
          if (!bucket) continue;          // happens only on legacy non-breakdown payloads
          out.push({ kind: 'loc', sku, loc, bucket });
        }
      }
    }
    return out;
  }, [tree, effectiveExpanded]);

  // Grand totals across all visible categories (the footer 合计 row).
  const totals = useMemo(() => {
    if (!tree.length) return null;
    const t = { produced: 0, sold: 0, wasted: 0, opening: 0, transferNet: 0 };
    for (const cat of tree) {
      t.produced    += cat.totals.produced;
      t.sold        += cat.totals.sold;
      t.wasted      += cat.totals.wasted;
      t.opening     += (cat.totals.opening || 0);
      t.transferNet += (cat.totals.transferNet || 0);
    }
    const avail = availableOf(t);
    t.wasteRate   = t.produced > 0 ? t.wasted / t.produced : null;
    t.sellThrough = avail > 0 ? t.sold / avail : null;
    return t;
  }, [tree]);

  const skuCount = filteredSkus.length;
  const catCount = tree.length;

  async function doExport() {
    if (!report) return;
    setExporting(true); setErr('');
    try {
      const opts = { utc: true };
      if (shift === 'day' || shift === 'night') opts.shift = shift;
      if (location !== 'all') opts.location = location;
      const fromUtc = torontoMidnightUtcIso(from);
      const toUtc   = torontoEndExclusiveUtcIso(to);
      const pivot = await api.loadInventoryPivot(fromUtc, toUtc, opts);
      const shiftLabel = SHIFT_OPTIONS.find(o => o.key === shift)?.exportSuffix || '全部';
      const locLabel   = LOCATION_OPTIONS.find(o => o.key === location)?.exportSuffix || '全部';
      // Filename composes both filters so multiple exports don't collide.
      await downloadXlsx({ pivot, fileLabel: `${shiftLabel}-${locLabel}` });
    } catch (e) {
      setErr(e?.message || '导出失败');
    } finally {
      setExporting(false);
    }
  }

  async function doExportFrontdesk() {
    setExportingFd(true); setErr('');
    try {
      const fromUtc = torontoMidnightUtcIso(from);
      const toUtc   = torontoEndExclusiveUtcIso(to);
      const report = await api.loadFrontdeskReport(fromUtc, toUtc);
      const hasData = (report.stores || []).some(s => Object.keys(report.sold?.[s] || {}).length > 0);
      if (!hasData) { setToast({ kind: 'error', msg: '所选区间内没有售出记录' }); return; }
      await downloadFrontdeskXlsx(report);
    } catch (e) {
      setErr(e?.message || '导出失败');
    } finally {
      setExportingFd(false);
    }
  }

  async function confirmWipe() {
    if (!wipeTarget) return;
    try {
      const r = await api.wipeProductEvents(wipeTarget.id);
      setToast({ kind: 'success', msg: `已清除「${wipeTarget.name}」的 ${r.deleted} 条记录` });
      await load();
    } catch (e) {
      setToast({ kind: 'error', msg: e?.message || '删除失败' });
    } finally {
      setWipeTarget(null);
    }
  }

  return (
    <>
      {/* ===== Header ===== */}
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">统计报表</h1>
          <div className="vava-page-meta">
            按产品分组的过去库存 / 当日生产 / 调拨 / 售出 / 报废 / 剩余 + 报废率 + 售罄率 · 可导出 Excel（匹配印刷模板）
          </div>
        </div>
        {mode === 'stock' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={doExportFrontdesk}
                  disabled={!report || exportingFd}
                  title="按门店 · 按日 导出 6寸 + Cupcakes 销售量（收入待产品价格录入）">
            {exportingFd ? <><span className="inv-spinner" aria-hidden="true" />导出中…</> : '⬇ 前台汇报'}
          </button>
          <button className="btn btn-primary" onClick={doExport}
                  disabled={!report || skuCount === 0 || exporting}>
            {exporting ? <><span className="inv-spinner" aria-hidden="true" />导出中…</> : '⬇ 导出 Excel'}
          </button>
        </div>
        )}
      </div>

      {/* ===== Report mode: 库存统计 (生产/售出/报废) | 备货分析 (plan vs reality) ===== */}
      <div className="inv-segmented pa-mode" role="tablist" aria-label="报表模式">
        <button role="tab" aria-selected={mode === 'stock'} onClick={() => setMode('stock')}>库存统计</button>
        <button role="tab" aria-selected={mode === 'prep'} onClick={() => setMode('prep')}>备货分析</button>
      </div>

      {mode === 'prep' ? <PrepAnalysis /> : (
      <>
      {/* ===== Toolbar: shift + location + search ===== */}
      <div className="inv-toolbar">
        <div className="inv-segmented" role="tablist" aria-label="班次筛选">
          {SHIFT_OPTIONS.map(o => (
            <button key={o.key}
                    role="tab"
                    aria-selected={shift === o.key}
                    onClick={() => changeShift(o.key)}
                    disabled={loading && shift !== o.key}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="inv-segmented" role="tablist" aria-label="门店筛选">
          {LOCATION_OPTIONS.map(o => (
            <button key={o.key}
                    role="tab"
                    aria-selected={location === o.key}
                    onClick={() => changeLocation(o.key)}
                    disabled={loading && location !== o.key}
                    title={o.key === 'CK' ? '中央厨房（生产）' :
                           o.key === 'all' ? '汇总所有门店' : `${o.key} 门店`}>
              {o.label}
            </button>
          ))}
        </div>
        <div className="inv-search">
          <input type="text"
                 value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="搜 产品 / 品类 / 口味 / 规格…" />
        </div>
        <div className="inv-tree-actions">
          <button onClick={expandAll}   disabled={!report}>全部展开</button>
          <button onClick={collapseAll} disabled={!report}>全部收起</button>
        </div>
        <span className="vava-hint" style={{ marginLeft: 8 }}>
          {catCount} 类 · {skuCount} SKU
        </span>
      </div>

      {/* ===== Date range filter ===== */}
      <div className="inv-filter-card">
        {PRESETS.map(p => (
          <button key={p.key}
                  className={preset === p.key ? 'btn btn-primary' : 'btn btn-secondary'}
                  onClick={() => applyPreset(p.key)}
                  disabled={loading}>
            {p.label}
          </button>
        ))}
        <span className="inv-divider" />
        <label className="vava-hint">从</label>
        <input type="date" className="vava-input inv-date-input"
               value={from} onChange={(e) => setFrom(e.target.value)} disabled={loading} />
        <label className="vava-hint">到</label>
        <input type="date" className="vava-input inv-date-input"
               value={to} onChange={(e) => setTo(e.target.value)} disabled={loading} />
        <button className="btn btn-secondary" onClick={applyCustom} disabled={loading}>查询</button>
        <label className="inv-checkbox-label">
          <input type="checkbox" checked={includeInactive} disabled={loading}
                 onChange={(e) => { setIncludeInactive(e.target.checked);
                                     load(from, to, shift, location, e.target.checked); }} />
          包括已停售
        </label>
      </div>

      {err && <div className="att-error inv-error" role="alert">{err}</div>}

      {/* ===== Stats table (3-level tree: 品类 → SKU → 门店) ===== */}
      {/* Date-range-empty banner: rows exist but every number is zero in
          this window. Common when window is too narrow (e.g. last 7 days
          on a slow week). Suggests widening to the next preset. */}
      {report && tree.length > 0 && totals && (totals.produced + totals.sold + totals.wasted === 0) && (
        <div className="att-warning inv-info-banner" role="status">
          <strong>所选区间内没有库存事件</strong> · {from} → {to} ·
          试试 <button className="inv-link-btn" onClick={() => applyPreset('7d')}>最近 7 天</button>
          或 <button className="inv-link-btn" onClick={() => applyPreset('30d')}>最近 30 天</button>
        </div>
      )}

      {!report ? (
        <div className="vava-card empty-state">加载中…</div>
      ) : flatRows.length === 0 ? (
        <div className="inv-grid-wrap">
          <div className="inv-empty">
            <div className="emoji">{search ? '🔍' : '📦'}</div>
            <div className="title">
              {search ? `没有匹配「${search}」的产品` : '所选筛选下没有产品'}
            </div>
            <div className="sub">
              {search
                ? '清空搜索框或换个关键词'
                : '调整 班次 / 门店 / 已停售 筛选'}
            </div>
          </div>
        </div>
      ) : (
        <div className="inv-grid-wrap">
          <table className="inv-grid">
            <thead>
              <tr>
                <SortHeader k="category"    label="品类 / 产品" sort={sort} onSort={changeSort} />
                <SortHeader k="variant"     label="口味" sort={sort} onSort={changeSort} />
                <SortHeader k="size"        label="规格" sort={sort} onSort={changeSort} />
                <SortHeader k="shift"       label="班次" sort={sort} onSort={changeSort} />
                <SortHeader k="opening"     label="过去库存" num sort={sort} onSort={changeSort} />
                <SortHeader k="produced"    label="当日生产" num sort={sort} onSort={changeSort} />
                <SortHeader k="transferNet" label="调拨"    num sort={sort} onSort={changeSort} />
                <SortHeader k="sold"        label="售出"    num sort={sort} onSort={changeSort} />
                <SortHeader k="wasted"      label="报废"    num sort={sort} onSort={changeSort} />
                <SortHeader k="remaining"   label="剩余"    num sort={sort} onSort={changeSort} />
                <SortHeader k="wasteRate"   label="报废率"  num sort={sort} onSort={changeSort} />
                <SortHeader k="sellThrough" label="售罄率"  num sort={sort} onSort={changeSort} />
                {isAdmin && <th className="num">操作</th>}
              </tr>
            </thead>
            <tbody>
              {flatRows.map((row, i) => {
                if (row.kind === 'cat') {
                  const { cat } = row;
                  const open = effectiveExpanded.has(`cat:${cat.name}`);
                  return (
                    <tr key={`cat-${cat.name}`} className="tree-cat">
                      <td colSpan={4}>
                        <span className="inv-tree-cell">
                          <button className={`inv-chevron ${open ? 'expanded' : ''}`}
                                  onClick={() => toggleExpand(`cat:${cat.name}`)}
                                  aria-label={open ? '收起' : '展开'}>▶</button>
                          <strong>{cat.name}</strong>
                          <span className="inv-tree-count">{cat.skus.length} SKU</span>
                        </span>
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: (cat.totals.opening || 0) ? 'var(--vava-text)' : 'var(--vava-text-subtle)' }}>
                        {cat.totals.opening || 0}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: cat.totals.produced ? 'var(--vava-green)' : 'var(--vava-text-subtle)' }}>
                        {cat.totals.produced}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: transferColor(cat.totals.transferNet) }}>
                        {fmtTransfer(cat.totals.transferNet)}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: cat.totals.sold ? 'var(--vava-tan-hover)' : 'var(--vava-text-subtle)' }}>
                        {cat.totals.sold}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: cat.totals.wasted ? 'var(--vava-red)' : 'var(--vava-text-subtle)' }}>
                        {cat.totals.wasted}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: remainingColor(remainingOf(cat.totals)), fontWeight: 600 }}>
                        {remainingOf(cat.totals)}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: wasteRateColor(cat.totals.wasteRate), fontWeight: 600 }}>
                        {formatRate(cat.totals.wasteRate)}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: sellThroughColor(cat.totals.sellThrough), fontWeight: 600 }}>
                        {formatRate(cat.totals.sellThrough)}
                      </td>
                      {isAdmin && <td></td>}
                    </tr>
                  );
                }
                if (row.kind === 'sku') {
                  const r = row.sku;
                  const open = effectiveExpanded.has(`sku:${r.id}`);
                  const hasPerStore = !!r.byLocation;
                  return (
                    <tr key={`sku-${r.id}`} className="tree-sku">
                      <td>
                        <span className="inv-tree-cell indent-1">
                          {hasPerStore ? (
                            <button className={`inv-chevron ${open ? 'expanded' : ''}`}
                                    onClick={() => toggleExpand(`sku:${r.id}`)}
                                    aria-label={open ? '收起门店' : '展开门店'}>▶</button>
                          ) : (
                            <span className="inv-chevron spacer">▶</span>
                          )}
                          {displayName(r)}
                          {!r.active && (
                            <span className="inv-shift-tag" style={{ marginLeft: 6 }}>已停售</span>
                          )}
                        </span>
                      </td>
                      <td className="inv-name-sub">{r.variant || '—'}</td>
                      <td className="inv-name-sub">{r.size || '—'}</td>
                      <td className="inv-name-sub">
                        {r.shift === 'day' ? '白班' : r.shift === 'night' ? '晚班' : '通用'}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: (r.opening || 0) ? 'var(--vava-text)' : 'var(--vava-text-subtle)' }}>
                        {r.opening || 0}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: r.produced ? 'var(--vava-green)' : 'var(--vava-text-subtle)' }}>
                        {r.produced}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: transferColor(r.transferNet) }}>
                        {fmtTransfer(r.transferNet)}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: r.sold ? 'var(--vava-tan-hover)' : 'var(--vava-text-subtle)' }}>
                        {r.sold}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: r.wasted ? 'var(--vava-red)' : 'var(--vava-text-subtle)' }}>
                        {r.wasted}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: remainingColor(remainingOf(r)), fontWeight: 600 }}>
                        {remainingOf(r)}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: wasteRateColor(r.wasteRate), fontWeight: 600 }}>
                        {formatRate(r.wasteRate)}
                      </td>
                      <td className="inv-cell-num readonly"
                          style={{ color: sellThroughColor(r.sellThrough), fontWeight: 600 }}>
                        {formatRate(r.sellThrough)}
                      </td>
                      {isAdmin && (
                        <td className="inv-row-actions">
                          <button onClick={() => setWipeTarget({ id: r.id, name: r.name, count: r.produced + r.sold + r.wasted })}
                                  title="清除此 SKU 的所有库存记录（测试数据用）"
                                  className="inv-wipe-btn"
                                  disabled={r.produced + r.sold + r.wasted === 0}>
                            🗑 清除
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                }
                // row.kind === 'loc' — per-store breakdown row
                const { loc, bucket } = row;
                const rate = bucket.produced > 0 ? bucket.wasted / bucket.produced : null;
                const availLoc = availableOf(bucket);
                const sellRate = availLoc > 0 ? bucket.sold / availLoc : null;
                return (
                  <tr key={`loc-${row.sku.id}-${loc}`} className="tree-loc">
                    <td colSpan={4}>
                      <span className="inv-tree-cell indent-2">
                        <span className="inv-chevron spacer">▶</span>
                        <span className="inv-tree-loc-label">@ {loc}</span>
                      </span>
                    </td>
                    <td className="inv-cell-num readonly">{(bucket.opening || 0) || '·'}</td>
                    <td className="inv-cell-num readonly">{bucket.produced || '·'}</td>
                    <td className="inv-cell-num readonly" style={{ color: transferColor(bucket.transferNet) }}>{fmtTransfer(bucket.transferNet)}</td>
                    <td className="inv-cell-num readonly">{bucket.sold || '·'}</td>
                    <td className="inv-cell-num readonly">{bucket.wasted || '·'}</td>
                    <td className="inv-cell-num readonly">{remainingOf(bucket) || '·'}</td>
                    <td className="inv-cell-num readonly">{formatRate(rate)}</td>
                    <td className="inv-cell-num readonly">{formatRate(sellRate)}</td>
                    {isAdmin && <td></td>}
                  </tr>
                );
              })}
            </tbody>
            {totals && (
              <tfoot>
                <tr className="inv-totals-row">
                  <td colSpan={4}>合计</td>
                  <td className="inv-cell-num readonly">{totals.opening || 0}</td>
                  <td className="inv-cell-num readonly">{totals.produced}</td>
                  <td className="inv-cell-num readonly" style={{ color: transferColor(totals.transferNet) }}>{fmtTransfer(totals.transferNet)}</td>
                  <td className="inv-cell-num readonly">{totals.sold}</td>
                  <td className="inv-cell-num readonly">{totals.wasted}</td>
                  <td className="inv-cell-num readonly" style={{ color: remainingColor(remainingOf(totals)) }}>
                    {remainingOf(totals)}
                  </td>
                  <td className="inv-cell-num readonly" style={{ color: wasteRateColor(totals.wasteRate) }}>
                    {formatRate(totals.wasteRate)}
                  </td>
                  <td className="inv-cell-num readonly" style={{ color: sellThroughColor(totals.sellThrough) }}>
                    {formatRate(totals.sellThrough)}
                  </td>
                  {isAdmin && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      <div className="inv-footnote">
        剩余 = 过去库存 + 当日生产 + 调拨 − 售出 − 报废（= 区间末在手）· 过去库存 = 开窗前结余 · 调拨 = 区间内净调入/调出（全部门店时为 0）· 报废率 = 报废数 ÷ 生产数 · 售罄率 = 售出数 ÷ 可售（过去库存 + 当日生产 + 调拨）· 区间内事件按事件日期统计（UTC）·
        导出 Excel 含「生产 / 售出 / 报废 / 汇总」四个工作表，与印刷模板格式一致。
        {isAdmin && <> · 🗑 仅管理员 / 店长可见 — 清除会删除该 SKU 在所有时间内的库存记录（含调拨双行）。</>}
      </div>

      {/* ===== Confirm dialog for destructive wipe ===== */}
      {wipeTarget && (
        <ConfirmDialog
          title="清除测试数据？"
          message={
            <>
              将删除「<strong>{wipeTarget.name}</strong>」的全部库存记录（生产/售出/报废/调拨）。
              当前 {wipeTarget.count} 件相关库存数据将归零。
              <br /><br />
              <strong style={{ color: 'var(--vava-red)' }}>此操作不可撤销。</strong>
            </>
          }
          confirmLabel="清除"
          kind="danger"
          onConfirm={confirmWipe}
          onClose={() => setWipeTarget(null)} />
      )}

      </>
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onDone={() => setToast(null)} />}
    </>
  );
};

// ============================================================================
// SortHeader — clickable <th> with asc/desc indicator.
// ============================================================================

const SortHeader = ({ k, label, num, sort, onSort }) => {
  const active = sort.key === k;
  const arrow = !active ? '' : (sort.dir === 'asc' ? '↑' : '↓');
  return (
    <th className={`sortable ${num ? 'num' : ''}`} onClick={() => onSort(k)}>
      {label} {arrow && <span className="sort-ind">{arrow}</span>}
    </th>
  );
};
